import { useState, Fragment, useContext, useEffect } from "react";
import Box from "@mui/material/Box";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import StepContent from "@mui/material/StepContent";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { BaseMatchState, GAME_TYPE } from "../types/match";
import { cloneDeep, uniq } from "lodash";
import DartBoardIcon from "./dartBoardIcon";
import { InOut } from "../types/x01";
import styled from "@mui/styled-engine";
import colors from "../theme/colors";
import fonts from "../theme/fonts";
import { mediaQueries } from "../theme/breakpoints";
import { OnlineMatchContext } from "../contexts/OnlineMatchContext";
import { PrivateMatchRequest } from "../types/online";
import { STEPPER_LABEL } from "../types/labels";

const initSteps = [
  {
    label: STEPPER_LABEL.ROOM_TYPE,
  },
];

interface StepperProps {
  matchState: BaseMatchState;
  selectGameType: Function;
  selectMatchType: Function;
  selectX01Game: Function;
  resetMatchState: Function;
  startMatch: Function;
  selectX01InOut: Function;
}

export default function OnlineGameStepper({
  matchState,
  selectGameType,
  selectX01Game,
  resetMatchState,
  selectX01InOut,
}: StepperProps) {
  const {
    handleFindCricket,
    handleSetOnline,
    handleFind301,
    handleFind501,
    handleJoinPrivate,
    handleCreatePrivate,
  } = useContext(OnlineMatchContext);
  const [activeStep, setActiveStep] = useState(0);
  const [steps, setSteps] = useState(cloneDeep(initSteps));
  const [privateMatch, setPrivateMatch] = useState(false);
  const [hostPrivate, setHostPrivate] = useState(false);
  const [gameType, setGameType] = useState(matchState.gameType);
  const [roomCode, setRoomCode] = useState("");
  const [x01Score, setX01Score] = useState(301);
  const [showCustomX01, setShowCustomX01] = useState(false);
  const [customX01Score, setCustomX01Score] = useState(5);
  const [inOutOption, setInOutOption] = useState<InOut>(InOut.ANY_IN_ANY_OUT);
  const [disableStart, setDisableStart] = useState(false);

  useEffect(() => {
    if (matchState.gameType !== gameType) {
      setGameType(matchState.gameType);
    }
    // eslint-disable-next-line
  }, [matchState.gameType]);

  const handleSetX01Game = (x01Game: number) => {
    setX01Score(x01Game);
    selectX01Game(x01Game);
    handleNext();
  };

  const handleSetX01InOut = (inOut: InOut) => {
    setInOutOption(inOut);
    selectX01InOut(inOut);
    handleNext();
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    if (activeStep === 1) {
      setSteps(cloneDeep(initSteps));
      return;
    }
    if (activeStep === 2) {
      if (privateMatch) {
        setSteps((steps) =>
          uniq(
            steps.filter(
              ({ label }) =>
                label !== STEPPER_LABEL.ROOM_CODE &&
                label !== STEPPER_LABEL.SELECT_DART_GAME
            )
          )
        );
        return;
      }
      setSteps((steps) =>
        uniq(steps.filter(({ label }) => label !== STEPPER_LABEL.ROOM_CODE))
      );
      return;
    }
    if (activeStep < 4) {
      setSteps((steps) =>
        uniq(
          steps.filter(
            ({ label }) =>
              label !== STEPPER_LABEL.X01_SCORE &&
              label !== STEPPER_LABEL.IN_OUT
          )
        )
      );
    }
  };

  const handleStart = async () => {
    setDisableStart(true);
    const { gameType } = matchState;
    if (!privateMatch) {
      switch (gameType) {
        case GAME_TYPE.CRICKET:
          await handleFindCricket();
          break;
        case GAME_TYPE.X01:
          if (x01Score === 301) {
            await handleFind301();
            break;
          }
          await handleFind501();
          break;
      }
      setDisableStart(false);
      return;
    }
    const matchOptions: PrivateMatchRequest = {
      gameType: gameType,
      x01Game: gameType === GAME_TYPE.X01 ? x01Score : undefined,
      inOut: gameType === GAME_TYPE.X01 ? inOutOption : undefined,
    };
    await handleCreatePrivate(matchOptions);
    setDisableStart(false);
    setActiveStep(0);
  };

  const handleReset = () => {
    setSteps(cloneDeep(initSteps));
    resetMatchState();
    setActiveStep(0);
  };

  const handleGoOffline = () => {
    handleSetOnline(false);
  };

  const handlePublic = () => {
    setPrivateMatch(false);
    const newSteps = [...initSteps, { label: STEPPER_LABEL.SELECT_DART_GAME }];
    setSteps(uniq(newSteps));
    setActiveStep(1);
  };

  const handlePrivate = () => {
    setPrivateMatch(true);
    const newSteps = [...initSteps, { label: STEPPER_LABEL.HOST_JOIN }];
    setSteps(uniq(newSteps));
    setActiveStep(1);
  };

  const onClickJoinPrivate = () => {
    setHostPrivate(false);
    const newSteps = [...steps, { label: STEPPER_LABEL.ROOM_CODE }];
    setSteps(uniq(newSteps));
    setActiveStep(2);
  };

  const onClickHostPrivate = () => {
    setHostPrivate(true);
    const newSteps = [...steps, { label: STEPPER_LABEL.SELECT_DART_GAME }];
    setSteps(uniq(newSteps));
    setActiveStep(2);
  };

  const handlePublicCricket = async () => {
    selectGameType(GAME_TYPE.CRICKET);
    handleNext();
  };

  const handlePublic301 = async () => {
    selectGameType(GAME_TYPE.X01);
    setX01Score(301);
    selectX01Game(301);
    setInOutOption(InOut.DOUBLE_IN_DOUBLE_OUT);
    selectX01InOut(InOut.DOUBLE_IN_DOUBLE_OUT);
    handleNext();
  };

  const handlePublic501 = async () => {
    selectGameType(GAME_TYPE.X01);
    setX01Score(501);
    selectX01Game(501);
    setInOutOption(InOut.ANY_IN_DOUBLE_OUT);
    selectX01InOut(InOut.ANY_IN_DOUBLE_OUT);
    handleNext();
  };

  const onClickPrivateCricket = async () => {
    handleNext();
    selectGameType(GAME_TYPE.CRICKET);
  };

  const onClickPrivateX01 = async () => {
    selectGameType(GAME_TYPE.X01);
    const newSteps = [
      ...steps,
      { label: STEPPER_LABEL.X01_SCORE },
      { label: STEPPER_LABEL.IN_OUT },
    ];
    setSteps(uniq(newSteps));
    handleNext();
  };

  return (
    <Wrapper>
      <Stepper activeStep={activeStep} orientation="vertical">
        <Step key={steps[0].label}>
          <StepLabel icon={<DartBoardIcon active={activeStep === 0} />}>
            <Label active={(activeStep === 0).toString()}>
              {steps[0].label}
            </Label>
          </StepLabel>
          <StepContent>
            <Box sx={{ mb: 2 }}>
              <Box display="flex">
                <OptionButton
                  variant="contained"
                  onClick={handlePublic}
                  size="large"
                  sx={{ mt: 1, mr: 1 }}
                >
                  Public
                </OptionButton>
                <OptionButton
                  variant="contained"
                  onClick={handlePrivate}
                  size="large"
                  sx={{ mt: 1, mr: 1 }}
                >
                  Private
                </OptionButton>
                <BackButton
                  variant="contained"
                  onClick={handleGoOffline}
                  size="large"
                  sx={{ mt: 1, mr: 1 }}
                >
                  Offline
                </BackButton>
              </Box>
            </Box>
          </StepContent>
        </Step>
        {steps.length > 1 && (
          <Step key={steps[1].label}>
            <StepLabel icon={<DartBoardIcon active={activeStep === 1} />}>
              <Label active={(activeStep === 1).toString()}>
                {steps[1].label}
              </Label>
            </StepLabel>
            <StepContent>
              <Box sx={{ mb: 2 }}>
                <Box display="flex">
                  {privateMatch ? (
                    <Fragment>
                      <OptionButton
                        variant="contained"
                        onClick={onClickHostPrivate}
                        size="large"
                        sx={{ mt: 1, mr: 1 }}
                      >
                        Host
                      </OptionButton>
                      <OptionButton
                        variant="contained"
                        onClick={onClickJoinPrivate}
                        size="large"
                        sx={{ mt: 1, mr: 1 }}
                      >
                        Join
                      </OptionButton>
                      <BackButton
                        variant="contained"
                        onClick={handleBack}
                        size="large"
                        sx={{ mt: 1, mr: 1 }}
                      >
                        Back
                      </BackButton>
                    </Fragment>
                  ) : (
                    <Fragment>
                      <OptionButton
                        variant="contained"
                        onClick={handlePublicCricket}
                        size="large"
                        sx={{ mt: 1, mr: 1 }}
                      >
                        Cricket
                      </OptionButton>
                      <OptionButton
                        variant="contained"
                        onClick={handlePublic301}
                        size="large"
                        sx={{ mt: 1, mr: 1 }}
                      >
                        301
                      </OptionButton>
                      <OptionButton
                        variant="contained"
                        onClick={handlePublic501}
                        size="large"
                        sx={{ mt: 1, mr: 1 }}
                      >
                        501
                      </OptionButton>
                      <BackButton
                        variant="contained"
                        onClick={handleBack}
                        size="large"
                        sx={{ mt: 1, mr: 1 }}
                      >
                        Back
                      </BackButton>
                    </Fragment>
                  )}
                </Box>
              </Box>
            </StepContent>
          </Step>
        )}
        {steps.length > 2 && (
          <Step key={steps[2].label}>
            <StepLabel icon={<DartBoardIcon active={activeStep === 2} />}>
              <Label active={(activeStep === 2).toString()}>
                {steps[2].label}
              </Label>
            </StepLabel>
            <StepContent>
              <Box sx={{ mb: 2 }}>
                <Box display="flex">
                  {hostPrivate ? (
                    <Fragment>
                      <OptionButton
                        variant="contained"
                        onClick={onClickPrivateCricket}
                        size="large"
                        sx={{ mt: 1, mr: 1 }}
                      >
                        Cricket
                      </OptionButton>
                      <OptionButton
                        variant="contained"
                        onClick={onClickPrivateX01}
                        size="large"
                        sx={{ mt: 1, mr: 1 }}
                      >
                        X01
                      </OptionButton>
                      <BackButton
                        variant="contained"
                        onClick={handleBack}
                        size="large"
                        sx={{ mt: 1, mr: 1 }}
                      >
                        Back
                      </BackButton>
                    </Fragment>
                  ) : (
                    <Box display="flex" flexDirection="column" gap="5px">
                      <Box display="flex">
                        <CustomRoomCodeInput
                          value={roomCode}
                          maxLength={6}
                          onChange={(event: any) => {
                            const roomCode: string = event?.target?.value;
                            setRoomCode(roomCode.toUpperCase());
                          }}
                        />
                        <Box
                          display="flex"
                          flexDirection="column"
                          justifyContent="center"
                          style={{
                            color: colors.lightBeige,
                            fontFamily: fonts.family.chalk,
                            fontSize: fonts.size.mediumLarge,
                          }}
                        ></Box>
                      </Box>
                      <Box display="flex" justifyContent="space-evenly">
                        <OptionButton
                          variant="contained"
                          disabled={!roomCode || roomCode.length !== 6}
                          onClick={() => {
                            handleJoinPrivate(roomCode);
                          }}
                          size="large"
                          sx={{ mt: 1, mr: 1 }}
                        >
                          OK
                        </OptionButton>
                        <BackButton
                          variant="contained"
                          onClick={handleBack}
                          size="large"
                          sx={{ mt: 1, mr: 1 }}
                        >
                          Back
                        </BackButton>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            </StepContent>
          </Step>
        )}
        {gameType === GAME_TYPE.X01 && steps.length > 3 && (
          <Step key={`${steps?.[steps.length - 3]?.label}_x01`}>
            <StepLabel icon={<DartBoardIcon active={activeStep === 3} />}>
              <Label active={(activeStep === 3).toString()}>
                {steps?.[steps.length - 2]?.label}
              </Label>
            </StepLabel>
            <StepContent>
              <Box sx={{ mb: 2 }}>
                {!showCustomX01 && (
                  <Box display="flex" style={{ flexWrap: "wrap" }}>
                    <OptionButton
                      variant="contained"
                      onClick={() => handleSetX01Game(301)}
                      size="large"
                      sx={{ mt: 1, mr: 1 }}
                    >
                      301
                    </OptionButton>
                    <OptionButton
                      variant="contained"
                      onClick={() => handleSetX01Game(501)}
                      size="large"
                      sx={{ mt: 1, mr: 1 }}
                    >
                      501
                    </OptionButton>
                    <OptionButton
                      variant="contained"
                      onClick={() => setShowCustomX01(true)}
                      size="large"
                      sx={{ mt: 1, mr: 1 }}
                    >
                      Custom
                    </OptionButton>
                    <BackButton
                      variant="contained"
                      onClick={handleBack}
                      size="large"
                      sx={{ mt: 1, mr: 1 }}
                    >
                      Back
                    </BackButton>
                  </Box>
                )}
                {showCustomX01 && (
                  <Box display="flex" flexDirection="column" gap="5px">
                    <Box display="flex">
                      <CustomX01Input
                        type="number"
                        value={customX01Score}
                        min={1}
                        onChange={(event: any) => {
                          const score: number = parseInt(event?.target?.value);
                          if (!score) return;
                          setCustomX01Score(score);
                        }}
                      />
                      <Box
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        style={{
                          color: colors.lightBeige,
                          fontFamily: fonts.family.chalk,
                          fontSize: fonts.size.mediumLarge,
                        }}
                      >
                        01
                      </Box>
                    </Box>
                    <Box display="flex">
                      <OptionButton
                        variant="contained"
                        onClick={() => {
                          const customScore = parseInt(`${customX01Score}01`);
                          if (isNaN(customScore)) return;
                          setX01Score(customScore);
                          handleSetX01Game(customScore);
                        }}
                        size="large"
                        sx={{ mt: 1, mr: 1 }}
                      >
                        OK
                      </OptionButton>
                      <BackButton
                        variant="contained"
                        onClick={() => setShowCustomX01(false)}
                        size="large"
                        sx={{ mt: 1, mr: 1 }}
                      >
                        Back
                      </BackButton>
                    </Box>
                  </Box>
                )}
              </Box>
            </StepContent>
          </Step>
        )}
        {gameType === GAME_TYPE.X01 && steps.length > 3 && (
          <Step key={`${steps?.[steps.length - 1]?.label}_x01`}>
            <StepLabel icon={<DartBoardIcon active={activeStep === 4} />}>
              <Label active={(activeStep === 4).toString()}>
                {steps?.[steps.length - 1]?.label}
              </Label>
            </StepLabel>
            <StepContent>
              <Box sx={{ mb: 2 }}>
                <Box display="flex" flexDirection="column">
                  <InOutWrapper>
                    {Object.entries(InOut).map(([key, value]) => {
                      return (
                        <OptionButton
                          variant="contained"
                          key={key}
                          onClick={() => handleSetX01InOut(value)}
                          size="large"
                          sx={{ mt: 1, mr: 1 }}
                          style={{ width: "100%" }}
                        >
                          <Typography
                            style={{
                              fontFamily: fonts.family.nice,
                              fontSize: fonts.size.medium,
                            }}
                          >
                            {value}
                          </Typography>
                        </OptionButton>
                      );
                    })}
                  </InOutWrapper>
                  <BackButton
                    variant="contained"
                    onClick={handleBack}
                    size="large"
                    sx={{ mt: 1, mr: 1 }}
                  >
                    Back
                  </BackButton>
                </Box>
              </Box>
            </StepContent>
          </Step>
        )}
      </Stepper>
      {activeStep === steps.length && gameType !== GAME_TYPE.NOT_SELECTED && (
        <GameOverBox>
          <GameOverViewPaper elevation={6}>
            <GameDescriptionWrapper>
              <Box>Online</Box>
              <Box>{privateMatch ? "Private" : "Public"}</Box>
              {gameType === GAME_TYPE.CRICKET ? (
                <Box>Cricket</Box>
              ) : (
                <Fragment>
                  <Box>{x01Score}</Box>
                  <Box>{inOutOption}</Box>
                </Fragment>
              )}
            </GameDescriptionWrapper>
            <Box display="flex" justifyContent="space-evenly">
              <PaperButton
                style={{ backgroundColor: colors.darkGray }}
                variant="contained"
                onClick={() => handleStart()}
                sx={{ mt: 1, mr: 1 }}
                disabled={disableStart}
              >
                Start
              </PaperButton>
              <PaperButton
                style={{ backgroundColor: colors.darkRed }}
                variant="contained"
                onClick={handleBack}
                sx={{ mt: 1, mr: 1 }}
              >
                Back
              </PaperButton>
              <PaperButton
                style={{ backgroundColor: colors.darkRed }}
                variant="contained"
                onClick={handleReset}
                sx={{ mt: 1, mr: 1 }}
              >
                Reset
              </PaperButton>
            </Box>
          </GameOverViewPaper>
        </GameOverBox>
      )}
    </Wrapper>
  );
}

const Wrapper = styled(Box)({
  width: "60%",
  "@media (max-width: 650px)": {
    width: "90%",
  },
});

const OptionButton = styled(Button)({
  backgroundColor: colors.lightBeige,
  color: colors.darkGray,
  textTransform: "none",
  fontSize: fonts.size.medium,
  fontFamily: fonts.family.nice,
  "&:hover": {
    backgroundColor: "#c7ab8d",
  },
  [mediaQueries.mobile]: {
    // fontSize: getFontSizeString(8),
    // fontSize: getFontSizeString(.9),
  },
});

const BackButton = styled(Button)({
  backgroundColor: "#881918",
  color: "white",
  textTransform: "none",
  fontSize: fonts.size.medium,
  fontFamily: fonts.family.nice,
  "&:hover": {
    backgroundColor: "rgb(197, 98, 98)",
  },
  [mediaQueries.mobile]: {
    // fontSize: getFontSizeString(8),
    // fontSize: getFontSizeString(.9),
  },
});

const Label = styled(Typography)<{ active: string }>({
  fontSize: fonts.size.medium,
  fontFamily: fonts.family.chalk,
  [mediaQueries.desktop]: {
    fontSize: fonts.size.large,
  },
  [mediaQueries.mobile]: {
    fontSize: fonts.size.medium,
  },
});

const GameOverBox = styled(Box)({
  width: "100%",
  display: "flex",
  justifyContent: "center",
  marginTop: "30px",
});

const GameOverViewPaper = styled(Paper)({
  width: "50%",
  fontSize: fonts.size.medium,
  padding: "10px",
  fontFamily: fonts.family.chalk,
  backgroundColor: colors.lightBeige,
  "@media (max-width: 650px)": {
    width: "100%",
    fontSize: fonts.size.small,
  },
});

const GameDescriptionWrapper = styled(Box)({
  fontSize: fonts.size.medium,
  display: "flex",
  flexDirection: "column",
});

const InOutWrapper = styled(Box)({
  display: "grid",
  gridTemplateColumns: "1fr 1fr 1fr 1fr",
  gap: "5px",
  "@media (max-width: 600px)": {
    gridTemplateColumns: "1fr 1fr",
  },
});

const PaperButton = styled(Button)({
  fontFamily: fonts.family.nice,
  fontSize: fonts.size.medium,
  textTransform: "none",
});

const CustomRoomCodeInput = styled("input")({
  width: "100%",
  marginRight: "5px",
  fontFamily: fonts.family.chalk,
  fontSize: fonts.size.mediumLarge,
  textAlign: "center",
});

const CustomX01Input = styled("input")({
  width: "100px",
  marginRight: "5px",
  fontFamily: fonts.family.chalk,
  fontSize: fonts.size.mediumLarge,
  textAlign: "right",
});
